import { toHumanReadable } from 'beam/dist/utils/duration'
import { msToHHMMSS } from 'beam/dist/utils/time'
import moment from 'moment/moment'
import 'moment-timezone'
import EVENT_TYPE from '../../const/eventType'
import PropTypes from 'prop-types'
import Tooltip from 'beam/dist/Tooltip'
import { Rect, Text } from 'victory'

const getOffsetPosition = (type, containerWidth, size, areEventsRightAligned) => {
  switch (type.toLowerCase()) {
    //Center
    case EVENT_TYPE.TRANSCRIPTION_HIT:
      return size
    //Left
    case EVENT_TYPE.TALKOVER:
    case EVENT_TYPE.SILENCE:
      return areEventsRightAligned ? containerWidth - size * 2 : 0
    //Right
    case EVENT_TYPE.HOLD:
    case EVENT_TYPE.PAUSE:
    case EVENT_TYPE.COMMENT:
    default:
      return containerWidth - size
  }
}

// TODO use theme colors
const getBackgroundColor = type => {
  switch (type.toLowerCase()) {
    case EVENT_TYPE.TRANSCRIPTION_HIT:
      return '#FFC72C'
    case EVENT_TYPE.TALKOVER:
      return '#E53F05'
    case EVENT_TYPE.SILENCE:
      return '#FF9D00'
    case EVENT_TYPE.HOLD:
      return '#F768A1'
    case EVENT_TYPE.PAUSE:
      return '#279989'
    case EVENT_TYPE.COMMENT:
    default:
      return '#FCC5C0'
  }
}

const parseDefaultEvent = (
  { eventType, contactOffset, duration = 0 },
  size,
  heightUnitsPerMs,
  containerWidth,
  areEventsRightAligned,
) => ({
  type: eventType,
  start: msToHHMMSS(contactOffset),
  startMs: contactOffset,
  duration: toHumanReadable(moment.duration(duration)),
  timePosition: Math.round(contactOffset * heightUnitsPerMs),
  offsetPosition: getOffsetPosition(eventType, containerWidth, size, areEventsRightAligned),
  width: size,
  durationLength: Math.max(Math.round(duration * heightUnitsPerMs), size),
  initial: eventType.charAt(0).toUpperCase(),
  opacity: 1,
  backgroundColor: getBackgroundColor(eventType),
})

const parseCommentEvent = (event, size, heightUnitsPerMs, containerWidth, areEventsRightAligned) => {
  const { created = 0, tz = null, commentor = null, text = '' } = event
  return {
    ...parseDefaultEvent(event, size, heightUnitsPerMs, containerWidth, areEventsRightAligned),
    created:
      created && tz
        ? moment(created)
          .tz(tz)
          .format('L')
        : null,
    by: commentor ? `${commentor.lastName}, ${commentor.firstName}` : null,
    comment: text ? (text.length > 25 ? `${text.slice(0, 20)}...` : text) : '',
  }
}

const parseTranscriptionHitEvent = (
  { eventType, contactOffset, duration, confidence, source, phrase, category, channel },
  size,
  heightUnitsPerMs,
  containerWidth,
  eventMaxWidth,
  areEventsRightAligned,
) => ({
  type: eventType,
  start: msToHHMMSS(contactOffset),
  startMs: contactOffset,
  duration: toHumanReadable(moment.duration(duration)),
  timePosition: Math.round(contactOffset * heightUnitsPerMs),
  offsetPosition: getOffsetPosition(eventType, containerWidth, size, areEventsRightAligned),
  width: confidence * (eventMaxWidth / 100) - 1,
  durationLength: Math.max(Math.round(duration * heightUnitsPerMs), 1),
  initial: '',
  opacity: 0.6,
  backgroundColor: getBackgroundColor(eventType),

  //Will be translated
  source: source === 'Phonetics' ? 'phonetics' : 'speechToText',
  phrase,
  category,
  confidence: source === 'Transcription' ? '' : `${confidence}%`,
  channel,
})

const parseEvent = (event, size, heightUnitsPerMs, containerWidth, eventMaxWidth, areEventsRightAligned) => {
  switch (event.eventType) {
    case EVENT_TYPE.COMMENT:
      return parseCommentEvent(event, size, heightUnitsPerMs, containerWidth, areEventsRightAligned)
    case EVENT_TYPE.TRANSCRIPTION_HIT:
      return parseTranscriptionHitEvent(
        event,
        size,
        heightUnitsPerMs,
        containerWidth,
        eventMaxWidth,
        areEventsRightAligned,
      )
    default:
      return parseDefaultEvent(event, size, heightUnitsPerMs, containerWidth, areEventsRightAligned)
  }
}

const AudioEvent = ({
  event,
  heightUnitsPerMs,
  t,
  size,
  containerWidth,
  eventMaxWidth = containerWidth,
  setTime,
  areEventsRightAligned,
}) => {
  const {
    type,
    start,
    startMs,
    duration,
    timePosition,
    offsetPosition,
    durationLength,
    width,
    initial,
    backgroundColor,
    opacity,

    created,
    by,
    comment,

    source,
    phrase,
    category,
    confidence,
    channel,
  } = parseEvent(event, size, heightUnitsPerMs, containerWidth, eventMaxWidth, areEventsRightAligned)

  return (
    <Tooltip
      disableFocusListener
      title={(eventType => {
        switch (eventType) {
          case EVENT_TYPE.TRANSCRIPTION_HIT:
            return (
              <div>
                <div>{`${t('source')}: ${t(source)}`}</div>
                <div>{`${t('phrase')}: ${phrase}`}</div>
                <div>{`${t('category')}: ${category}`}</div>
                <div>{`${t('start')}: ${start}`}</div>
                <div>{`${t('duration')}: ${duration}`}</div>
                <div>{`${t('confidence')}: ${confidence}`}</div>
                <div>{`${t('channel')}: ${channel}`}</div>
              </div>
            )
          case EVENT_TYPE.COMMENT:
            return (
              <div>
                <div>{`${t('type')}: ${t(type.toLowerCase())}`}</div>
                <div>{`${t('start')}: ${start}`}</div>
                <div>{`${t('created')}: ${created}`}</div>
                <div>{`${t('by')}: ${by}`}</div>
                <div>{`${t('text')}: ${comment}`}</div>
              </div>
            )
          default:
            return (
              <div>
                <div>{`${t('type')}: ${t(type.toLowerCase())}`}</div>
                <div>{`${t('start')}: ${start}`}</div>
                <div>{`${t('duration')}: ${duration}`}</div>
              </div>
            )
        }
      })(type)}
    >
      <g onClick={() => setTime(startMs)} style={{ cursor: 'pointer' }}>
        <Rect
          x={offsetPosition}
          y={timePosition}
          width={width}
          height={durationLength}
          style={{
            fill: backgroundColor,
            opacity,
          }}
        />
        {initial && (
          <Text
            x={offsetPosition}
            y={timePosition}
            dx={size * 0.1875}
            dy={size * 0.75}
            style={{
              fontSize: size + 'px',
              fontFamily: 'monospace', // TODO add theme monospacing
            }}
          >
            {initial}
          </Text>
        )}
      </g>
    </Tooltip>
  )
}

AudioEvent.propTypes = {
  containerWidth: PropTypes.number.isRequired,
  event: PropTypes.object.isRequired,
  eventMaxWidth: PropTypes.number,
  heightUnitsPerMs: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  size: PropTypes.number.isRequired,
  setTime: PropTypes.func,
  areEventsRightAligned: PropTypes.bool,
}

AudioEvent.defaultProps = {
  setTime: () => {},
  areEventsRightAligned: false,
}

export { AudioEvent as UnstyledAudioEvent }

export default AudioEvent
